import type { CrmEvent } from '@plato/data/prisma';
import { endOfDay, formatDate, formatISO, setDay, startOfToday } from 'date-fns';
import type {
    RRule as RRuleClass,
    RRuleSet as RRuleSetClass,
    Weekday as WeekdayClass,
    WeekdayStr,
} from 'rrule';
import * as rrule from 'rrule';

type RRuleLib = {
    RRule: typeof RRuleClass;
    RRuleSet: typeof RRuleSetClass;
    Weekday: typeof WeekdayClass;
    rrulestr: typeof import('rrule').rrulestr;
};

// overcome the bad packaging of rrule
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion, @typescript-eslint/no-unsafe-member-access -- legacy code
const rruleLib = ((rrule as any).default ? (rrule as any).default : rrule) as RRuleLib;
const { RRule, RRuleSet, Weekday, rrulestr } = rruleLib;

export const DEFAULT_INTERVAL = 2;

export const getDefaultRecurrence = () => {
    return new RRule({
        freq: RRule.WEEKLY,
        interval: DEFAULT_INTERVAL,
    }).toString();
};

export const getRecurrenceWeekday = (date: Date) => {
    const dayOfWeek = date.getDay();

    switch (dayOfWeek) {
        case 1:
            return RRule.MO;
        case 2:
            return RRule.TU;
        case 3:
            return RRule.WE;
        case 4:
            return RRule.TH;
        case 5:
            return RRule.FR;
        case 6:
            return RRule.SA;
        case 7:
            return RRule.SU;
        default:
            return RRule.SU;
    }
};

export const getRecurrenceWeekdayNumber = (weekday: WeekdayClass): number => {
    switch (weekday) {
        case RRule.MO:
            return 1;
        case RRule.TU:
            return 2;
        case RRule.WE:
            return 3;
        case RRule.TH:
            return 4;
        case RRule.FR:
            return 5;
        case RRule.SA:
            return 6;
        default:
            return 0;
    }
};

export function getRecurrenceWeeksCount(recurrence: string, startDate: string) {
    return parseRecurrenceString(recurrence, startDate)?.interval ?? 0;
}

export type RecurrenceOptions = {
    /**
     * The interval(in weeks) of the recurrence.
     */
    interval: number;

    /**
     * The weekdays of the recurrence (the same that Date.getDay() returns).
     */
    weekdays?: number[];

    /**
     * The excluded dates of the recurrence in ISO date format (YYYY-MM-DD).
     */
    excludedDates: string[];

    /**
     * The last date (inclusive) on which the recurrence is allowed to produce
     * an occurrence, in ISO date format (YYYY-MM-DD).
     */
    until?: string;
};

/**
 * The typed recurrence columns of `CrmEvent` (Decision 1). schema.prisma documents
 * their semantics: `recurrenceWeeks` NULL means not recurring, and weekdays are
 * ISO 8601 (Postgres ISODOW). `RecurrenceOptions` is the in-memory shape. The two
 * mappers below are the single translation point between the shapes, and the only
 * place that converts between JS weekdays and ISO weekdays.
 */
export type RecurrenceColumns = Pick<
    CrmEvent,
    'recurrenceWeeks' | 'recurrenceWeekdays' | 'recurrenceUntil' | 'recurrenceExcludedDates'
>;

/** JS `Date.getDay()` (0=Sun..6=Sat) → ISO 8601 (1=Mon..7=Sun). */
export function jsDayToIso(jsDay: number): number {
    return jsDay === 0 ? 7 : jsDay;
}

/** ISO 8601 (1=Mon..7=Sun) → JS `Date.getDay()` (0=Sun..6=Sat). */
export function isoToJsDay(isoDay: number): number {
    return isoDay === 7 ? 0 : isoDay;
}

/** `@db.Date` columns come back as midnight-UTC instants. Read the date part as UTC. */
function dateColumnToIso(date: Date): string {
    return date.toISOString().slice(0, 10);
}

export function recurrenceOptionsToColumns(options: RecurrenceOptions): RecurrenceColumns {
    return {
        recurrenceWeeks: options.interval,
        recurrenceWeekdays: (options.weekdays ?? []).map(jsDayToIso),
        recurrenceUntil: options.until ? new Date(options.until) : null,
        recurrenceExcludedDates: options.excludedDates.map((iso) => new Date(iso)),
    };
}

export function recurrenceColumnsToOptions(columns: RecurrenceColumns): RecurrenceOptions | undefined {
    if (columns.recurrenceWeeks === null) {
        return undefined;
    }

    return {
        interval: columns.recurrenceWeeks,
        // Map an empty array to undefined: an empty `byweekday` produces zero occurrences
        // in rrule. An empty stored set must regenerate as "no BYDAY" (weekday = DTSTART).
        weekdays:
            columns.recurrenceWeekdays.length > 0 ? columns.recurrenceWeekdays.map(isoToJsDay) : undefined,
        until: columns.recurrenceUntil ? dateColumnToIso(columns.recurrenceUntil) : undefined,
        excludedDates: columns.recurrenceExcludedDates.map(dateColumnToIso),
    };
}

function untilDate(iso: string): Date {
    return new Date(`${iso}T23:59:59Z`);
}

export function generateRecurrenceString(options: RecurrenceOptions, startDate: string): string {
    const rrule = new RRule({
        freq: RRule.WEEKLY,
        interval: options.interval,
        byweekday: options.weekdays?.map((day) => getRecurrenceWeekday(setDay(new Date(startDate), day))),
        until: options.until ? untilDate(options.until) : undefined,
    });

    if (options.excludedDates.length === 0) {
        return rrule.toString();
    }

    return `${rrule.toString()}\nEXDATE:${options.excludedDates.map((date) => formatDate(date, 'yyyyMMdd')).join(',')}`;
}

function parseRecurrenceStringInternal(recurrence: string, startDate: string): RRuleSetClass | RRuleClass {
    return rrulestr(`DTSTART:${formatDate(new Date(startDate), 'yyyyMMdd')}T000000Z\n${recurrence}\n`);
}

export function parseRecurrenceString(recurrence: string, startDate: string): RecurrenceOptions | undefined {
    if (!recurrence || !startDate) {
        return undefined;
    }

    try {
        const rruleMaybeSet = parseRecurrenceStringInternal(recurrence, startDate);

        const excludedDates: string[] = [];

        if (rruleMaybeSet instanceof RRuleSet) {
            for (const date of rruleMaybeSet.exdates()) {
                excludedDates.push(formatISO(date, { representation: 'date' }));
            }
        }

        const rrule = rruleMaybeSet instanceof RRuleSet ? rruleMaybeSet.rrules()[0] : rruleMaybeSet;

        if (rrule?.options.freq !== RRule.WEEKLY) {
            return undefined;
        }

        const interval = rrule.options.interval;

        if (typeof interval !== 'number') {
            return undefined;
        }

        const weekdays = rrule.options.byweekday.map((weekday: number | string | WeekdayClass) => {
            if (typeof weekday === 'number') {
                // rrule uses 0-6 for weekdays, where 0 is Monday, but in JS 0 is Sunday
                return weekday === 6 ? 0 : weekday + 1;
            }

            // Theoretically, this is not needed, but there was cases where weekdays was not an array of numbers
            if (typeof weekday === 'string') {
                return getRecurrenceWeekdayNumber(Weekday.fromStr(weekday as WeekdayStr));
            }

            return getRecurrenceWeekdayNumber(weekday);
        });

        const until = rrule.options.until ? rrule.options.until.toISOString().slice(0, 10) : undefined;

        return {
            weekdays,
            interval,
            excludedDates,
            until,
        };
    } catch (error) {
        console.error('Failed to parse recurrence string', recurrence, error);
        return undefined;
    }
}

export function generateOccurences(
    recurrence: string,
    startDate: string,
    [fromIso, toIso]: [string, string],
): string[] {
    const rruleMaybeSet = parseRecurrenceStringInternal(recurrence, startDate);

    const from = new Date(fromIso);
    const to = endOfDay(new Date(toIso));

    return rruleMaybeSet.between(from, to, true).map((d: Date) => formatISO(d, { representation: 'date' }));
}

export function getNextOccurence(
    recurrence: string,
    startDate: string,
    including: boolean,
): string | undefined {
    const rruleMaybeSet = parseRecurrenceStringInternal(recurrence, startDate);

    const nextDate = rruleMaybeSet.after(startOfToday(), including);

    return nextDate ? formatISO(nextDate, { representation: 'date' }) : undefined;
}
